import formatDate from 'date-fns/format'

import {
  uploadCreativeFile,
  createCampaign,
  getCampaign,
  updateCampaign,
  getAdvertiser,
  getCampaigns,
  getSegment,
  getAdvertiserTargetingCountries,
  getAdtechFeatureFlag,
} from '@/api/ads'
import { SPONSORED_STORE_ID } from '@/constants/placement-data'
import { ADTEC_USER_TYPE } from '@/constants/adtech-general'
import { countryData } from '@/constants/country-data'
import { getMessagesByLocale } from '@/locales'
import { COUNTRY_CODE_KEY_MAP } from '@/views/ads/constants/i18n'

/**
 * Error codes for targeting countries API
 */
export const TARGETING_COUNTRIES_ERROR = {
  NO_COUNTRIES_AVAILABLE: 404, // Merchant doesn't have billing account in adtech enabled countries
  NOT_ALLOWED: 406, // Either postpaid is not enabled or not selected billing countries for postpaid
}

const getDefaultState = () => {
  return {
    adsPlacementsData: [],
    budgetingData: {},
    isFetchingPlacement: false,
    lastError: undefined,
    detail: {},
    isFetchingDetail: false,
    targeting: {},
    isFetchingAdvertiser: false,
    advertiser: null,
    paymentData: null,
    userType: ADTEC_USER_TYPE.NEW,
    adsSegment: null,
    isFetchingAdsSegment: false,
    targetAudienceSegments: null,
    underMaintenance: false,
    underMaintenanceFrom: null,
    underMaintenanceTo: null,
    campaignFormMode: null,
    isAdsTopUpBannerEnabled: false,
    isAdsPciPaymentEnabled: false,
    isFlashDealEnabled: false,
    isTreasureDealEnabled: false,
    isFetchingAdsFeatureFlag: false,
    hasFetchedAdsFeatureFlag: false,
    targetingCountriesError: null, // Stores error code (404 or 406) when targeting countries API fails
  }
}

const normalizeAdsFeatureFlags = (data = {}) => {
  return {
    isAdtechEnabled: Boolean(data?.isAdtechEnabled),
    isTopUpBannerEnabled: Boolean(
      data?.isTopUpBannerEnabled ?? data?.isAdsTopUpBannerEnabled
    ),
    isPciPaymentEnabled: Boolean(data?.isPciPaymentEnabled),
    isFlashDealEnabled: Boolean(data?.isFlashDealEnabled),
    isTreasureDealEnabled: Boolean(data?.isTreasureDealEnabled),
    underMaintenance: Boolean(data?.underMaintenance),
    underMaintenanceFrom: data?.underMaintenanceFrom ?? null,
    underMaintenanceTo: data?.underMaintenanceTo ?? null,
  }
}

const mutations = {
  SET_ADS_PLACEMENTS_DATA(state, { data }) {
    state.adsPlacementsData = data
  },
  SET_BUDGETING_DATA(state, { data }) {
    state.budgetingData = data
  },
  SET_IS_FETCHING(state, { name, value }) {
    state[name] = value
  },
  SET_ERROR(state, error) {
    state.lastError = error
  },
  SET_CAMPAIGN_DETAIL(state, { data }) {
    state.detail = data
  },
  SET_TARGETING(state, targeting) {
    state.targeting = targeting
  },
  SET_ADVERTISER(state, { data }) {
    state.advertiser = data
  },
  SET_PAYMENT_DATA(state, data) {
    state.paymentData = data
  },
  SET_USER_TYPE(state, data) {
    state.userType = data
  },
  SET_ADS_SEGMENT(state, { data }) {
    state.adsSegment = data
  },
  SET_TARGET_AUDIENCE_SEGMENTS(state, audienceSegments) {
    state.targetAudienceSegments = audienceSegments
  },
  RESET_ALL_DATA(state) {
    // Advertiser should be kept between pages
    Object.assign(state, {
      ...getDefaultState(),
      advertiser: state.advertiser,
      userType: state.userType,
    })
  },
  SET_UNDER_MAINTENANCE(state, data) {
    state.underMaintenance = data.underMaintenance
    state.underMaintenanceTo = data.underMaintenanceTo
    state.underMaintenanceFrom = data.underMaintenanceFrom
  },
  SET_CAMPAIGN_FORM_MODE(state, { data }) {
    state.campaignFormMode = data
  },
  SET_ADS_TOP_UP_BANNER_ENABLED(state, isEnabled) {
    state.isAdsTopUpBannerEnabled = isEnabled
  },
  SET_ADS_PCI_PAYMENT_ENABLED(state, isEnabled) {
    state.isAdsPciPaymentEnabled = isEnabled
  },
  SET_FLASH_DEAL_ENABLED(state, isEnabled) {
    state.isFlashDealEnabled = isEnabled
  },
  SET_TREASURE_DEAL_ENABLED(state, isEnabled) {
    state.isTreasureDealEnabled = isEnabled
  },
  SET_HAS_FETCHED_ADS_FEATURE_FLAG(state, value) {
    state.hasFetchedAdsFeatureFlag = value
  },
  SET_TARGETING_COUNTRIES_ERROR(state, errorCode) {
    state.targetingCountriesError = errorCode
  },
}

const getters = {
  adsPlacementsData: (state) => state.adsPlacementsData,
  isFetchingPlacement: (state) => state.isFetchingPlacement,
  budgetingData: (state) => state.budgetingData,
  lastError: (state) => state.lastError,
  detail: (state) => state.detail,
  isFetchingDetail: (state) => state.isFetchingDetail,
  targeting: (state) => state.targeting,
  advertiser: (state) => state.advertiser ?? {},
  advertiserCountry: (_, getters) => getters.advertiser?.country || 'SA',
  advertiserCountryData(_, getters) {
    return countryData.find(
      (country) => country.countryCode === getters.advertiserCountry
    )
  },
  currency(_, getters) {
    return getters.advertiserCountryData?.currency || 'SAR'
  },
  isFetchingAdvertiser: (state) => state.isFetchingAdvertiser,
  paymentData: (state) => state.paymentData,
  userType: (state) => state.userType,
  isFetchingAdsSegment: (state) => state.isFetchingAdsSegment,
  adsSegment: (state) => state.adsSegment,
  targetAudienceSegments: (state) => state.targetAudienceSegments,
  underMaintenance: (state) => state.underMaintenance,
  underMaintenanceFrom: (state) => state.underMaintenanceFrom,
  underMaintenanceTo: (state) => state.underMaintenanceTo,
  campaignFormMode: (state) => state.campaignFormMode,
  isAdsPciPaymentEnabled: (state) => state.isAdsPciPaymentEnabled,
  isFlashDealEnabled: (state) => state.isFlashDealEnabled,
  isTreasureDealEnabled: (state) => state.isTreasureDealEnabled,
  isFetchingAdsFeatureFlag: (state) => state.isFetchingAdsFeatureFlag,
  hasFetchedAdsFeatureFlag: (state) => state.hasFetchedAdsFeatureFlag,
  targetingCountriesError: (state) => state.targetingCountriesError,
}

const actions = {
  async createAdsPlacements({ commit }, { data }) {
    try {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingPlacement',
        value: true,
      })
      const res = await uploadCreativeFile(data)
      return res?.data?.data
    } catch (error) {
      commit('SET_ERROR', error?.response?.data)
      throw error
    } finally {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingPlacement',
        value: false,
      })
    }
  },
  setBudgetingData({ commit }, { data }) {
    commit('SET_BUDGETING_DATA', { data })
  },
  setPaymentData({ commit }, data) {
    commit('SET_PAYMENT_DATA', data)
  },
  setCampaignFormMode({ commit }, data) {
    commit('SET_CAMPAIGN_FORM_MODE', { data })
  },
  async submitCampaign(
    { commit, state },
    { campaignName, placementsData, budgetingData, storeCPC, bannerCPC }
  ) {
    const data = {
      name: campaignName,
      ...budgetingData,
      placements: placementsData.map((el) => ({
        creative_id: el.id,
        targeting: {
          languages: [el.language],
        },
        creative_metadata: {
          advertiser_internal_id: el.metadata.advertiserInternalId,
          merchant_id: el.metadata.merchantId,
          store_logo: el.metadata.storeLogo,
          store_name: el.metadata.storeName,
          language: el.metadata.language,
        },
        placement_id: el.placementId,
        placement_type: el.placementType,
        price: el.placementId === SPONSORED_STORE_ID ? storeCPC : bannerCPC,
      })),
      targeting: {
        ...state.targeting,
      },
      segments: state.targetAudienceSegments,
    }

    if (budgetingData.start_date) {
      const startDate = formatDate(
        new Date(budgetingData.start_date),
        'yyyy-MM-dd'
      )
      data.start_date = `${startDate}T00:00:00Z`
    }

    if (budgetingData.end_date) {
      const endDate = formatDate(new Date(budgetingData.end_date), 'yyyy-MM-dd')
      data.end_date = `${endDate}T23:59:59Z`
    }

    const res = await createCampaign(data)
    return res
  },
  async fetchCampaignDetail({ commit }, { campaignId }) {
    try {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingDetail',
        value: true,
      })
      const res = await getCampaign(campaignId)
      if (res?.data?.data) {
        commit('SET_CAMPAIGN_DETAIL', { data: res?.data?.data })
        return res?.data?.data
      }
    } finally {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingDetail',
        value: false,
      })
    }
  },
  setTargeting({ commit }, targeting) {
    commit('SET_TARGETING', targeting)
  },
  async updateCampaign(
    { state, dispatch },
    {
      targetAudiencesData,
      segments,
      placementsData,
      budgetingData,
      campaignId,
      campaignName,
      storeCPC,
      bannerCPC,
    }
  ) {
    const originalPlacements = state?.detail?.placements || []
    const placements = placementsData.map((el) => {
      const indexSelected = originalPlacements.findIndex(
        (item) =>
          item.placementId === el.placementId &&
          item.targeting.languages.includes(el.language)
      )

      const item = {
        creative_id: el.id,
        creative_metadata: {
          advertiser_internal_id: el.metadata.advertiserInternalId,
          merchant_id: el.metadata.merchantId,
          store_logo: el.metadata.storeLogo,
          store_name: el.metadata.storeName,
          language: el.metadata.language,
        },
        placement_id: el.placementId,
        placement_type: el.placementType,
        price: el.placementId === SPONSORED_STORE_ID ? storeCPC : bannerCPC,
        title: el.title,
        click_url: el.clickUrl,
        image_url: el.imageLink,
        targeting: {
          languages: [el.language],
        },
      }
      if (indexSelected > -1) {
        item.external_id = originalPlacements[indexSelected].externalId
      }

      return item
    })

    const data = {
      id: state.detail.id,
      external_advertiser_id: state.detail.externalAdvertiserId,
      external_id: state.detail.externalId,
      review_status: state.detail.reviewStatus,
      review_message_ar: state.detail.reviewMessageAr,
      review_message_en: state.detail.reviewMessageEn,
      risk_level: state.detail.riskLevel,
      status: state.detail.status,
      ...budgetingData,
      is_active: state.detail.isActive,
      created_at: state.detail.createdAt,
      updated_at: state.detail.updatedAt,
      placements,
      targeting: targetAudiencesData,
      name: campaignName,
      segments,
    }

    if (budgetingData.start_date) {
      const startDate = formatDate(
        new Date(budgetingData.start_date),
        'yyyy-MM-dd'
      )
      data.start_date = `${startDate}T00:00:00Z`
    }

    if (budgetingData.end_date) {
      const endDate = formatDate(new Date(budgetingData.end_date), 'yyyy-MM-dd')
      data.end_date = `${endDate}T23:59:59Z`
    }

    const res = await updateCampaign({
      campaignId,
      data,
    })
    return res
  },
  async fetchAdvertiser({ commit }) {
    try {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingAdvertiser',
        value: true,
      })
      const res = await getAdvertiser()

      if (res?.data?.data) {
        commit('SET_ADVERTISER', { data: res?.data?.data })
        return res?.data?.data
      }
    } catch (_) {
      // TODO: handle it later
    } finally {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingAdvertiser',
        value: false,
      })
    }
  },
  async checkUserType({ commit, state, dispatch }) {
    if (state.underMaintenance) {
      return null
    }

    let userType = null
    try {
      // In case advertiser didn't fetch yet
      // fetch and check if advertiser created
      if (!state.advertiser) {
        const advertiser = await dispatch('fetchAdvertiser')

        if (Object.keys(advertiser).length === 0) {
          return null
        }
      }

      const res = await getCampaigns({
        page: 1,
        pageSize: 1,
      })

      userType =
        res?.data?.data.length > 0
          ? ADTEC_USER_TYPE.EXISTING
          : ADTEC_USER_TYPE.NEW

      commit('SET_USER_TYPE', userType)
    } catch {}

    return userType
  },
  async fetchSegment({ commit, state }, { currentLocale }) {
    try {
      commit('SET_ADS_SEGMENT', {
        data: null,
      })
      commit('SET_TARGETING_COUNTRIES_ERROR', null)
      commit('SET_IS_FETCHING', {
        name: 'isFetchingAdsSegment',
        value: true,
      })

      // Call both APIs in parallel
      const [segmentRes, countriesRes] = await Promise.all([
        getSegment(),
        getAdvertiserTargetingCountries()
          .then((res) => {
            if (!res?.data?.data?.length) {
              commit(
                'SET_TARGETING_COUNTRIES_ERROR',
                TARGETING_COUNTRIES_ERROR.NO_COUNTRIES_AVAILABLE
              )
            }
            return res
          })
          .catch((error) => {
            const statusCode = error?.response?.status
            if (
              statusCode === TARGETING_COUNTRIES_ERROR.NO_COUNTRIES_AVAILABLE ||
              statusCode === TARGETING_COUNTRIES_ERROR.NOT_ALLOWED
            ) {
              commit('SET_TARGETING_COUNTRIES_ERROR', statusCode)
            }
          }),
      ])

      const titleName = currentLocale === 'en_US' ? 'nameEn' : 'nameAr'
      const descriptionName =
        currentLocale === 'en_US' ? 'descriptionEn' : 'descriptionAr'

      const segmentData = segmentRes.data.data

      const ageGroups = segmentData.ageGroups.map((el) => ({
        title: el[titleName],
        value: el?.values?.[0],
      }))

      const genders = segmentData.genders.map((el) => ({
        title: el[titleName],
        value: el?.values?.[0],
      }))

      const localeMessages = getMessagesByLocale(currentLocale)
      const countries = (countriesRes?.data?.data || [])
        .map((countryCode) => {
          if (!countryCode || countryCode.toLowerCase() === 'unknown') {
            return null
          }

          const translationKey = COUNTRY_CODE_KEY_MAP[countryCode]
          const countryTitle = translationKey
            ? localeMessages[translationKey]
            : countryCode
          return {
            title: countryTitle || countryCode,
            value: countryCode,
          }
        })
        .filter(Boolean)

      if (countries.length === 0) {
        commit(
          'SET_TARGETING_COUNTRIES_ERROR',
          TARGETING_COUNTRIES_ERROR.NO_COUNTRIES_AVAILABLE
        )
      }

      const interests = segmentData.interests.map((el) => ({
        key: el.id,
        title: el[titleName],
        titleEN: el.nameEn,
        desc: el[descriptionName],
        isChecked: false,
        value: el?.values || [],
        type: el.type,
      }))

      const customInterests = segmentData.customInterests.map((el) => ({
        key: el.id,
        title: el[titleName],
        titleEN: el.nameEn,
        desc: el[descriptionName],
        isChecked: false,
        value: el?.values || [],
        type: el.type,
      }))

      if (segmentRes?.data?.data) {
        commit('SET_ADS_SEGMENT', {
          data: {
            ageGroups,
            genders,
            countries,
            interests: (interests || [])
              .concat(customInterests)
              .sort((prev, next) =>
                prev.titleEN
                  .toLowerCase()
                  .localeCompare(next.titleEN.toLowerCase())
              ),
          },
        })
      }
    } finally {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingAdsSegment',
        value: false,
      })
    }
  },
  async fetchAdsFeatureFlag({ commit, dispatch }) {
    try {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingAdsFeatureFlag',
        value: true,
      })
      // Feature flag from BE to enable AdTech module on FE
      const res = await getAdtechFeatureFlag()

      dispatch('setFeatureFlags', res)
    } finally {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingAdsFeatureFlag',
        value: false,
      })
    }
  },
  setTargetAudienceSegments({ commit }, audienceSegments) {
    commit('SET_TARGET_AUDIENCE_SEGMENTS', audienceSegments)
  },
  resetAllData({ commit }) {
    commit('RESET_ALL_DATA')
  },
  setFeatureFlags({ commit }, data) {
    const normalizedData = normalizeAdsFeatureFlags(data)

    commit('SET_UNDER_MAINTENANCE', normalizedData)

    // Should only enable Banner in case adtech enabled and not under maintainance
    commit(
      'SET_ADS_TOP_UP_BANNER_ENABLED',
      normalizedData.isTopUpBannerEnabled &&
        normalizedData.isAdtechEnabled &&
        !normalizedData.underMaintenance
    )

    commit(
      'SET_ADS_PCI_PAYMENT_ENABLED',
      normalizedData.isPciPaymentEnabled &&
        normalizedData.isAdtechEnabled &&
        !normalizedData.underMaintenance
    )

    commit(
      'SET_FLASH_DEAL_ENABLED',
      normalizedData.isFlashDealEnabled && normalizedData.isAdtechEnabled
    )
    commit(
      'SET_TREASURE_DEAL_ENABLED',
      normalizedData.isTreasureDealEnabled && normalizedData.isAdtechEnabled
    )
    commit('SET_HAS_FETCHED_ADS_FEATURE_FLAG', true)
  },
}

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations,
  actions,
  getters,
}
